import React, { useContext, useState, useEffect } from 'react';
import { AppContext } from '../App';
import { convertPrice } from '../data/mockData';
import { certificationService } from '../services';
import './Certifications.css';

const steps = [
  { icon: '📋', title: 'Enroll', desc: 'Pay and get matched to your expert mentor for the track.' },
  { icon: '🏗️', title: 'Build', desc: 'Receive your project brief and start coding daily with mentor guidance.' },
  { icon: '🔍', title: 'Review', desc: 'Your mentor reviews every submission with detailed code feedback.' },
  { icon: '🎤', title: 'Defend', desc: 'Pass a live 1-on-1 technical interview with your mentor.' },
  { icon: '🏆', title: 'Certify', desc: 'Receive your blockchain-verified certificate, verifiable by employers.' },
];

export default function Certifications() {
  const { setModalOpen, currency } = useContext(AppContext);
  const [CERTIFICATIONS, setCertifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    certificationService.getCertifications()
      .then(res => setCertifications(res.certifications || []))
      .catch(err => { console.error('Failed to load certifications:', err); setCertifications([]); })
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div style={{ display:'flex', justifyContent:'center', alignItems:'center', minHeight:'60vh', flexDirection:'column', gap:16 }}>
      <div style={{ fontSize:40 }}>⏳</div>
      <div style={{ color:'#6b7280' }}>Loading certifications...</div>
    </div>
  );
  return (
    <div className="certs-page">
      {/* Hero */}
      <div className="cert-hero">
        <div className="cert-hero-inner">
          <div className="section-label" style={{background:'rgba(255,255,255,.15)',borderColor:'rgba(255,255,255,.3)',color:'#fff'}}>Project-Based Certification</div>
          <h1 className="cert-hero-h1">Earn Certificates That<br/><span style={{color:'#86efac'}}>Employers Actually Trust</span></h1>
          <p>Not just course completion badges. Real projects, real code reviews, real interviews. Our certifications are verified on blockchain and backed by industry experts.</p>
          <div className="cert-hero-btns">
            <button className="btn btn-white btn-lg" onClick={() => setModalOpen(true)}>🚀 Enroll Now</button>
            <button className="btn btn-white-ghost btn-lg" onClick={() => document.getElementById('tracks').scrollIntoView({behavior:'smooth'})}>Browse Tracks ↓</button>
          </div>
        </div>
      </div>

      {/* Process */}
      <div className="section-wrap" style={{background:'#fff',maxWidth:'100%',borderBottom:'1px solid var(--line)'}}>
        <div style={{maxWidth:1200,margin:'0 auto',padding:'64px 32px'}}>
          <div style={{textAlign:'center',marginBottom:48}}>
            <div className="section-label">The Process</div>
            <h2 className="t-h2">5 Steps from Enrollment to Certificate</h2>
          </div>
          <div className="cert-flow">
            {steps.map((s, i) => (
              <div key={s.title} className="cf-step">
                <div className="cf-ico">{s.icon}</div>
                <div className="cf-num">{i + 1}</div>
                <h5>{s.title}</h5>
                <p>{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Tracks */}
      <div id="tracks" style={{background:'var(--surface)',borderBottom:'1px solid var(--line)'}}>
        <div style={{maxWidth:1200,margin:'0 auto',padding:'64px 32px'}}>
          <div style={{textAlign:'center',marginBottom:40}}>
            <div className="section-label">All Tracks</div>
            <h2 className="t-h2">Choose Your Certification Path</h2>
          </div>
          <div className="tracks-grid">
            {CERTIFICATIONS.map(t => (
              <div key={t.id} className={`track-card ${t.popular ? 'popular' : ''}`}>
                {t.popular && <div className="popular-badge">⭐ Most Popular</div>}
                <div className="track-ico">{t.icon}</div>
                <div className="track-name">{t.name}</div>
                <p className="track-desc">{t.desc}</p>
                <div className="track-skills">
                  {t.skills.map(s => <span key={s} className="badge badge-gray">{s}</span>)}
                </div>
                <div className="track-foot">
                  <div>
                    <span className="track-dur">⏱ {t.duration}</span>
                    <div style={{fontSize:'.72rem',color:'var(--ink4)',marginTop:2}}>👥 {t.enrolled.toLocaleString()} enrolled</div>
                  </div>
                  <div style={{textAlign:'right'}}>
                    <div className="track-price">{convertPrice(t.priceINR, currency)}</div>
                    <button className="btn btn-primary btn-sm" onClick={() => setModalOpen(true)}>Enroll Now</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Certificate preview */}
      <div style={{background:'#fff',borderBottom:'1px solid var(--line)'}}>
        <div style={{maxWidth:1200,margin:'0 auto',padding:'64px 32px',textAlign:'center'}}>
          <div className="section-label">Certificate Preview</div>
          <h2 className="t-h2" style={{marginBottom:12}}>What Your Certificate Looks Like</h2>
          <p style={{color:'var(--ink3)',marginBottom:40}}>Verified on blockchain · Shareable URL · PDF download · LinkedIn badge</p>
          <div className="cert-preview">
            <div className="cert-top">
              <div className="cert-logo-row">
                <div className="cert-logo-ico">D</div>
                <div style={{fontWeight:800,fontSize:'1rem'}}>DevBrains</div>
              </div>
              <div style={{fontSize:'.68rem',letterSpacing:'.15em',textTransform:'uppercase',opacity:.75,marginBottom:6}}>Certificate of Achievement</div>
              <div style={{fontSize:'.9rem',opacity:.8}}>This certifies that</div>
              <div style={{fontSize:'1.9rem',fontWeight:800,margin:'12px 0 6px'}}>Your Name Here</div>
              <div style={{opacity:.7}}>has successfully completed</div>
              <div style={{fontSize:'1.1rem',fontWeight:800,marginTop:8,color:'#86efac'}}>Full-Stack Web Development · React + Node.js</div>
            </div>
            <div className="cert-bottom">
              <div className="cert-meta">
                <div style={{fontSize:'1.2rem',marginBottom:3}}>📅</div>
                <div style={{fontSize:'.62rem',fontWeight:800,textTransform:'uppercase',letterSpacing:'.08em',color:'var(--ink4)'}}>Date</div>
                <div style={{fontSize:'.78rem',fontWeight:700,color:'var(--ink)',marginTop:1}}>Jan 2025</div>
              </div>
              <div className="cert-meta">
                <div style={{fontSize:'1.2rem',marginBottom:3}}>⏱</div>
                <div style={{fontSize:'.62rem',fontWeight:800,textTransform:'uppercase',letterSpacing:'.08em',color:'var(--ink4)'}}>Duration</div>
                <div style={{fontSize:'.78rem',fontWeight:700,color:'var(--ink)',marginTop:1}}>10 Weeks</div>
              </div>
              <div className="cert-meta">
                <div style={{fontSize:'1.2rem',marginBottom:3}}>🔗</div>
                <div style={{fontSize:'.62rem',fontWeight:800,textTransform:'uppercase',letterSpacing:'.08em',color:'var(--ink4)'}}>Verified</div>
                <div style={{fontSize:'.78rem',fontWeight:700,color:'var(--green-d)',marginTop:1}}>Blockchain</div>
              </div>
              <div className="cert-meta">
                <div style={{fontSize:'1.2rem',marginBottom:3}}>⭐</div>
                <div style={{fontSize:'.62rem',fontWeight:800,textTransform:'uppercase',letterSpacing:'.08em',color:'var(--ink4)'}}>Grade</div>
                <div style={{fontSize:'.78rem',fontWeight:700,color:'var(--ink)',marginTop:1}}>Distinction</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
